/**
 * Accent-insensitive search — the SINGLE source of truth (D-1, D-4).
 *
 * The defect: Prisma's `contains` compiles to `ILIKE`, which folds CASE but
 * never ACCENTS. `search=Ouédraogo` returned 13 clients on preprod while
 * `search=ouedraogo` returned 0 — and Ouédraogo is the most common surname
 * in Burkina Faso, one that phone keyboards do not spell with its accent.
 *
 * Two roads were closed before this one:
 *   - ICU non-deterministic collations DO fold accents for equality, but
 *     Postgres rejects them for `LIKE` outright ("nondeterministic
 *     collations are not supported for LIKE"), and `contains` IS `LIKE`.
 *   - The `unaccent` extension is marked STABLE, not IMMUTABLE (verified:
 *     `pg_proc.provolatile = 's'`), so Postgres refuses it in an index —
 *     and it is an extension, which this design deliberately needs nowhere.
 *     It must NOT be installed.
 *
 * What is left, and what this module encodes: `lower(translate(...))`. Both
 * functions are core builtins marked IMMUTABLE (`provolatile = 'i'`), so the
 * folded form can be materialised by the DATABASE in an ordinary column
 * kept current by a trigger — which every environment gets from the same
 * guard SQL, which no write path can leave stale, and which the existing
 * Prisma `contains` searches with no raw SQL at all. Tenant
 * isolation (I6) therefore stays in the typed `where` object where it
 * belongs, instead of moving into concatenated `$queryRaw` strings across
 * dozens of call sites.
 *
 * ── THE INVARIANT THIS FILE EXISTS TO HOLD ───────────────────────────
 * The SQL that FILLS the column and the TypeScript that NORMALISES the
 * user's term must fold characters identically, forever. So neither is
 * written by hand: both are derived from `ACCENT_FOLDING_PAIRS` below.
 * `packages/api/src/db/search-index.ts` generates the DDL from it;
 * `normalizeSearchTerm` applies it to the query. Editing the map changes
 * both at once, and the guards' backfill — an `UPDATE … WHERE col IS
 * DISTINCT FROM <expression>` — repairs exactly the affected rows on the
 * next boot, with no version bookkeeping to remember.
 */
/**
 * Accented character → its ASCII fold, as PAIRS rather than two parallel
 * strings. `translate(string, from, to)` silently DELETES characters whose
 * position in `from` has no counterpart in `to`, so two hand-aligned string
 * literals of unequal length do not fail loudly — they quietly eat letters.
 * Pairs cannot drift apart.
 *
 * Ligatures (œ, æ) are deliberately absent: `translate` maps one character
 * to one character and cannot expand « œ » to « oe ». Folding them to a
 * single letter would be worse than leaving them alone.
 */
const ACCENT_FOLDING_PAIRS = [
    ["à", "a"], ["â", "a"], ["ä", "a"], ["ã", "a"], ["á", "a"], ["å", "a"],
    ["é", "e"], ["è", "e"], ["ê", "e"], ["ë", "e"],
    ["í", "i"], ["ì", "i"], ["î", "i"], ["ï", "i"],
    ["ó", "o"], ["ò", "o"], ["ô", "o"], ["ö", "o"], ["õ", "o"],
    ["ú", "u"], ["ù", "u"], ["û", "u"], ["ü", "u"],
    ["ç", "c"], ["ñ", "n"], ["ý", "y"], ["ÿ", "y"],
    ["À", "A"], ["Â", "A"], ["Ä", "A"], ["Ã", "A"], ["Á", "A"], ["Å", "A"],
    ["É", "E"], ["È", "E"], ["Ê", "E"], ["Ë", "E"],
    ["Í", "I"], ["Ì", "I"], ["Î", "I"], ["Ï", "I"],
    ["Ó", "O"], ["Ò", "O"], ["Ô", "O"], ["Ö", "O"], ["Õ", "O"],
    ["Ú", "U"], ["Ù", "U"], ["Û", "U"], ["Ü", "U"],
    ["Ç", "C"], ["Ñ", "N"], ["Ý", "Y"],
];
/** The `from` argument of `translate()`. Derived — never written by hand. */
export const ACCENT_FOLD_FROM = ACCENT_FOLDING_PAIRS.map((p) => p[0]).join("");
/** The `to` argument of `translate()`. Same length as `ACCENT_FOLD_FROM`. */
export const ACCENT_FOLD_TO = ACCENT_FOLDING_PAIRS.map((p) => p[1]).join("");
/**
 * Whitespace characters that fold to one plain space, a run of them to a
 * single space: tab, line breaks, the no-break space and the narrow
 * no-break space French formatting puts in amounts and before « : ». Listed
 * explicitly because `\s` differs between JavaScript and Postgres.
 */
const WHITESPACE_FOLD_CHARS = [
    " ", "\t", "\n", "\r", "\f", "\v", " ", " ",
];
/**
 * The bracket expression matching a run of folded whitespace, valid as
 * both a JavaScript and a Postgres regular expression.
 */
export const WHITESPACE_FOLD_PATTERN = `[${WHITESPACE_FOLD_CHARS.join("")}]+`;
const WHITESPACE_FOLD_REGEX = new RegExp(WHITESPACE_FOLD_PATTERN, "g");
/** The column name, identical on every model in scope. */
export const SEARCH_INDEX_COLUMN = "searchIndex";
/**
 * D-2 — THE SCOPE. Model → the columns folded into its search blob.
 *
 * The line is drawn at REFERENTIALS THE USER SEARCHES BY PROPER NAME: the
 * seven pickers where somebody types a person's or a product's name from
 * memory, on a phone, without accents. Not every `mode: "insensitive"` in
 * the codebase is user-facing search — `authenticate.ts` and
 * `qrcode.service.ts` compare strings exactly, `Establishment.nimPrefix`
 * matches a DGI-issued identifier, and `Employee.department` is an
 * exact-match dropdown filter. Folding an identifier would be a defect, not
 * a fix.
 *
 * Each blob lists EXACTLY the columns already present in that entity's
 * current `OR`, so behaviour changes for accents and for nothing else.
 * One consequence is accepted deliberately: a blob is a concatenation, so a
 * term spanning two adjacent fields ("ouedraogo 70") now matches where the
 * per-field `OR` could not. That widening was judged harmless for a search
 * box.
 *
 * The Postgres identifiers here are QUOTED verbatim in generated DDL, so
 * they must match `schema.prisma` exactly (Prisma does not snake_case).
 */
export const SEARCH_INDEX_MODELS = {
    // erp-connectors — LocalClientRepository.findMany
    Client: ["name", "email", "phone", "ifu", "code"],
    // erp-connectors — LocalArticleRepository.findMany (code/name/description,
    // plus `category`, whose absence was the reported « je n'arrive pas à
    // ajouter d'articles » and whose « Réseau » still failed unaccented),
    // and the barcode a scanner types into the same box.
    Article: ["code", "name", "description", "category", "barcode"],
    // SupplierService.list
    Supplier: ["name", "ifu", "email", "phone"],
    // EmployeeService.list. The e-mail is personal data (data:personal):
    // EmployeeService matches it whole, for holders of that permission only.
    Employee: ["firstName", "lastName", "matricule", "position"],
    // LeadService.list
    Lead: ["name", "company", "email", "phone"],
    // ProjectService.list
    Project: ["code", "name"],
    // UserService.list — the same Burkinabè surnames as Client and Employee;
    // excluding it would make « Ouédraogo » findable in the client list and
    // not in the user list, which is the very incoherence being fixed.
    User: ["firstName", "lastName", "email"],
};
/**
 * Folds a string the way the database folds the stored column:
 * `translate()` FIRST, then `lower()`, then each run of whitespace to one
 * space — the same order, so that a term and a stored value can never
 * disagree about a character.
 */
export function normalizeSearchTerm(value) {
    let folded = "";
    for (const char of value) {
        const index = ACCENT_FOLD_FROM.indexOf(char);
        folded += index === -1 ? char : ACCENT_FOLD_TO[index];
    }
    return folded.toLowerCase().replace(WHITESPACE_FOLD_REGEX, " ");
}
/**
 * ★ D-4 — THE ONE WAY TO BUILD A SEARCH FILTER.
 *
 * Returns the Prisma fragment matching `term` accent- and case-insensitively
 * against a model in `SEARCH_INDEX_MODELS`, or `undefined` when the term is
 * empty (so callers can spread it without a branch).
 *
 * Note the deliberate absence of `mode: "insensitive"`: the stored column is
 * ALREADY lower-cased by the database, and the term is lower-cased here, so
 * a plain `LIKE` suffices. Asking Postgres for `ILIKE` on top would only pay
 * for case folding twice.
 *
 * The returned object carries NO tenant scope on purpose — I6 stays where it
 * already is, in the caller's `where: { tenantId, ... }`. This helper only
 * ever ADDS a predicate to an existing typed `where`; it never builds one.
 */
export function searchIndexFilter(term) {
    const trimmed = term?.trim();
    if (!trimmed) {
        return undefined;
    }
    return { searchIndex: { contains: normalizeSearchTerm(trimmed) } };
}
//# sourceMappingURL=search-index.js.map